import React, { useId, useMemo } from 'react';
import cx from 'classnames';
import { withBlockExtensions } from '@plone/volto/helpers';

import ContatosPorEstado from './ContatosPorEstado';
import { toContatosIndexados, toTelHref } from './helpers';
import { IMPRENSA_ROTULOS, type ImprensaData } from './schema';

interface MpfImprensaBlockViewProps {
  data: ImprensaData;
  isEditMode?: boolean;
  /** Presente só no editor: abre o contato de índice `index` na barra lateral. */
  onEditItem?: (index: number) => void;
}

const TITULO_PADRAO = 'Imprensa';

/** Um par rótulo/valor da coluna de atendimento. */
const Dado: React.FC<{
  rotulo: string;
  valor?: string;
  href?: string;
}> = ({ rotulo, valor, href }) => {
  if (!valor) return null;

  return (
    <div className="mpf-imprensa__dado">
      <dt className="mpf-imprensa__dado-rotulo">{rotulo}</dt>
      <dd className="mpf-imprensa__dado-valor">
        {href ? (
          <a className="mpf-imprensa__canal" href={href}>
            {valor}
          </a>
        ) : (
          valor
        )}
      </dd>
    </div>
  );
};

/** Bloco "Imprensa": atendimento a jornalistas + contatos das unidades. */
export const MpfImprensaBlockView: React.FC<MpfImprensaBlockViewProps> = ({
  data,
  isEditMode,
  onEditItem,
}) => {
  const tituloId = useId();
  const titulo = data?.titulo || TITULO_PADRAO;

  const contatos = useMemo(
    () => toContatosIndexados(data?.contatos),
    [data?.contatos],
  );

  const temAtendimento = Boolean(
    data?.atendimentoTexto ||
      data?.telefone ||
      data?.telefoneSecundario ||
      data?.email ||
      data?.horario,
  );

  if (!temAtendimento && contatos.length === 0) {
    return isEditMode ? (
      <section className="mpf-imprensa mpf-imprensa--vazio">
        <p className="mpf-imprensa__aviso">
          Preencha o atendimento a jornalistas ou ao menos um contato por estado
          na barra lateral. Vazio, o bloco não aparece na página.
        </p>
      </section>
    ) : null;
  }

  return (
    <section
      className={cx('mpf-imprensa not-prose', {
        'mpf-imprensa--destaque': data.variante === 'destaque',
      })}
      aria-labelledby={tituloId}
    >
      <header className="mpf-imprensa__cabecalho">
        <h2 id={tituloId} className="mpf-imprensa__titulo">
          {titulo}
        </h2>

        {/* Decorativa: quem nomeia a seção é o título acima. */}
        <div className="mpf-imprensa__regua" aria-hidden="true" />
      </header>

      <div className="mpf-imprensa__grid">
        {temAtendimento && (
          <section className="mpf-imprensa__coluna mpf-imprensa__coluna--atendimento">
            {data.atendimentoTitulo && (
              <h3 className="mpf-imprensa__coluna-titulo">
                {data.atendimentoTitulo}
              </h3>
            )}

            {data.atendimentoTexto && (
              <p className="mpf-imprensa__texto">{data.atendimentoTexto}</p>
            )}

            <dl className="mpf-imprensa__dados">
              <Dado
                rotulo={data.telefoneRotulo || IMPRENSA_ROTULOS.telefone}
                valor={data.telefone}
                href={toTelHref(data.telefone)}
              />
              <Dado
                rotulo={
                  data.telefoneSecundarioRotulo ||
                  IMPRENSA_ROTULOS.telefoneSecundario
                }
                valor={data.telefoneSecundario}
                href={toTelHref(data.telefoneSecundario)}
              />
              <Dado
                rotulo={data.emailRotulo || IMPRENSA_ROTULOS.email}
                valor={data.email}
                href={data.email ? `mailto:${data.email}` : undefined}
              />
              <Dado
                rotulo={data.horarioRotulo || IMPRENSA_ROTULOS.horario}
                valor={data.horario}
              />
            </dl>
          </section>
        )}

        {contatos.length > 0 && (
          <div className="mpf-imprensa__coluna mpf-imprensa__coluna--contatos">
            <ContatosPorEstado
              titulo={data.contatosTitulo}
              label={data.contatosLabel}
              placeholder={data.contatosPlaceholder}
              linkRotulo={data.contatosLinkRotulo}
              filtro={data.variante === 'destaque' ? 'estado' : 'busca'}
              contatos={contatos}
              onEditItem={onEditItem}
            />
          </div>
        )}
      </div>
    </section>
  );
};

export default withBlockExtensions(MpfImprensaBlockView);
